<template>
  <v-container class="ma-0 pa-0" fluid>
    <v-data-table
      dense
      :class="table"
      style="cursor:pointer"
      sort-by="ad_start_date"
      sort-desc
      :height="tableSize"
      :loading="loading"
      :headers="headers"
      :items.sync="filteredAdPlanners"
      :items-per-page="pageSize"
      item-key="adId"
      color="#fff"
      fixed-header
      loading-text="Loading... Please Wait"
      no-results-text="No Matching Ad Planners Found"
      :no-data-text="selectedStore ? 'No Ad Planners Found' : 'Please Select Store to View Ad Planners'"
      @click:row="viewAdPlanner"
      hide-default-footer>
      <template #top>
        <v-toolbar flat color="toolbar" height="75" style="cursor:default;">
          <v-toolbar-title>Ad Planners</v-toolbar-title>
          <v-divider vertical inset class="mx-4"/>
          <v-row dense align="center">
            <v-col md="4" lg="3">
              <v-autocomplete
                :class="body"
                auto-select-first
                v-model="selectedAdGroup"
                :items="userAdGroups"
                item-text="name"
                item-value="id"
                return-object
                hide-no-data
                class="mt-7"
                background-color="input"
                label="Ad Group"
                outlined
                dense
                prepend-inner-icon="mdi-filter-outline"
                :menu-props="{ maxHeight: 260 }"
                :rules="[v => !!v ||  'Required']">
                <template v-slot:item="data">
                  <template>
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ data.item.name }}
                      </v-list-item-title>
                      <v-list-item-subtitle class="text-capitalize">
                        Ad Group
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col cols="3">
            <v-autocomplete
              :class="body"
              auto-select-first
              v-model="selectedStore"
              :items="storeParties"
              item-text="name"
              item-value="id"
              return-object
              class="mt-7"
              background-color="input"
              placeholder="Select Store"
              label="Store"
              outlined
              dense
              prepend-inner-icon="mdi-filter-outline"
              :menu-props="{ maxHeight: 260, rounded: '' }"
              :disabled="!selectedAdGroup"
              :rules="[v => !!v ||  'Required']">
                <template v-slot:item="data">
                  <template>
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ data.item.name }}
                      </v-list-item-title>
                      <v-list-item-subtitle class="text-capitalize">
                        Store
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col v-if="getsSuggestedOrders" cols="2">
              <v-select
                :class="body"
                class="mt-7"
                auto-select-first
                v-model="suggestedFilter"
                :items="suggestedFilterOptions"
                background-color="input"
                placeholder="Select Store"
                label="Filter Ad Type"
                outlined
                dense
                prepend-inner-icon="mdi-filter-outline"
                :menu-props="{ maxHeight: 260, offsetY: true, rounded: '' }">
              </v-select>
            </v-col>
          </v-row>
        </v-toolbar>
      </template>

      <template #[`item.ad_details`]="{ item }">
        <v-menu
          open-on-hover
          offset-y
          bottom
          :close-on-content-click="false">
          <template #activator="{ on }">
            <v-btn
              v-on="on"
              icon
              @click.stop="">
              <v-icon>mdi-information</v-icon>
            </v-btn>
          </template>
          <v-card>
            <v-list>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-subtitle>Ad ID</v-list-item-subtitle>
                  <v-list-item-title>{{ item.ad_id }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-subtitle>Created On</v-list-item-subtitle>
                  <v-list-item-title>{{
                    formatDateTimestamp(item.created_on)
                  }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-subtitle>Created By</v-list-item-subtitle>
                  <v-list-item-title>{{ item.created_by }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card>
        </v-menu>
      </template>

      <template #[`item.ordering_status`]="{ item }">
        <v-chip class="mr-4" small dark :class="statusDisplay(item, 'color')">
          <span style=" display: inline-block; margin: 0 auto; width: 120px; text-align: center;">
            <span>{{ statusDisplay(item, 'text') }}</span>
          </span>
        </v-chip>
      </template>

      <template #[`item.last_shipper_date`]="{ item }">
        {{ formatDateDisplay(orderingDates(item, 'last_shipper')) }}
      </template>

      <template #[`item.last_order_date`]="{ item }">
        {{ formatDateDisplay(orderingDates(item, 'last_order')) }}
      </template>

      <template #[`item.ad_start_date`]="{ item }">
        {{formatDateDisplay(item.ad_start_date)}} - {{formatDateDisplay(item.ad_end_date)}}
      </template>

      <template #[`item.audit.updated_on`]="{ item }">
        {{formatDateTimestamp(item.audit.updated_on)}}
      </template>

      <template v-slot:footer>
        <v-divider></v-divider>
        <v-toolbar flat color="toolbar">
          <v-row align="center">
            <v-spacer></v-spacer>
            <v-col cols="2" style="max-width:150px;">
              <v-select
                class="mt-8"
                dense
                :items="pageSizes"
                label="Items Per Page"
                v-model="pageSize"
                :menu-props="{ top: true, offsetY: true, maxHeight: 500 }">
              </v-select>
            </v-col>
            <v-btn
              icon
              large
              @click="previous"
              :disabled="disablePrevious"
              class="mx-2">
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <small>Page {{ page }}</small>
            <v-btn icon large
              @click="next"
              :disabled="disableNext"
              class="mx-2">
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </v-row>
        </v-toolbar>
      </template>
    </v-data-table>
  </v-container>
</template>
<script>
// api
import AdRetails from '@/axios/ad-retails'
import Party from '@/axios/party'
// mixins
import { displayAlert } from '@/mixins/alert'
import { fullwidth } from '@/mixins/fullwidth'
import { partySearch } from '@/mixins/party-search'
import { paging } from '@/mixins/paging'
import { displayHelpers } from '@/mixins/displayHelpers'
import { userAccess } from '@/mixins/user-access'
import { utils } from '@/mixins/utils'
// third party
import moment from 'moment'

export default {
  name: 'RetailAdManager',
  mixins: [displayAlert, fullwidth, displayHelpers, partySearch, paging, userAccess, utils],
  data () {
    return {
      adDate: '',
      adPlanners: [],
      selectedAdGroup: null,
      storeParties: [],
      selectedStore: null,
      loading: false,
      menu: false,
      modal: false,
      suggestedFilterOptions: ['All', 'Meat', 'Grocery'],
      suggestedFilter: 'All',
      // Search-surface-style default: when the auto-selected first ad group has
      // no planners, probe the user's other groups and land on the first one
      // that has data (instead of an empty dead-end). Only runs when the user
      // hasn't explicitly picked a group (no session selection), and stops the
      // moment they touch the dropdowns.
      needsAutoPick: false,
      userPicked: false
    }
  },
  watch: {
    selectedAdGroup: {
      handler (newValue, oldValue) {
        if (newValue) {
          // A change not initiated by the auto-pick probe is a user choice —
          // stop probing so we never yank a selection out from under them.
          if (!this._autoAdvance) this.userPicked = Boolean(oldValue)
          this._autoAdvance = false
          sessionStorage.setItem('selected_group', JSON.stringify(newValue))
          if (oldValue) sessionStorage.removeItem('selected_store')
          this.getStoreParties()
        }
      },
      deep: true
    },
    selectedStore: {
      handler (newValue) {
        if (newValue) {
          sessionStorage.setItem('selected_store', JSON.stringify(newValue))
          this.getAdPlanners()
        }
      },
      deep: true
    },
    adDate: {
      handler () {
        this.getAdPlanners()
      }
    },
    userAdGroups: {
      handler (newValue) {
        if (newValue?.length > 0 && !this.selectedAdGroup) {
          if (this.tenant === 'pricechopper') {
            this.selectedAdGroup = newValue.find(obj => obj.name === 'PCE Ad Group')
          } else {
            this.selectedAdGroup = newValue[0]
          }
        }
        // Groups often arrive AFTER the first planner load. If that load came
        // back empty and the user hasn't touched anything, probe now.
        if (newValue?.length > 1 && this.needsAutoPick && !this.userPicked &&
            this._firstLoadDone && !this.loading && this.adPlanners.length === 0) {
          this.needsAutoPick = false
          this.autoPickGroupWithPlanners()
        }
      },
      deep: true
    },
    tenantStores: {
      handler (newValue) {
        if (newValue?.length > 0 && this.tenant === 'pricechopper' && this.selectedAdGroup.name === 'PCE Ad Group') {
          this.getStoreParties()
        }
      }
    },
    suggestedFilter: {
      handler (newValue) {
        if (newValue) {
          localStorage.setItem('suggested_filter', this.suggestedFilter)
        }
      }
    },
    activeUserRelatedPartyIds: {
      handler(newValue) {
        if (newValue.length > 0) {
          this.getStoreParties()
        }
      }
    }
  },
  beforeCreate () {
    this.$store.dispatch('getAdGroups')
  },
  created () {
    this.nonTableHeight = 235
    // Auto-pick runs at most once per visit, and only while the user hasn't
    // touched the dropdowns: if the landing group has no planners, we move to
    // the first group that does (search-surface behavior — never an empty
    // dead-end). Session-restored groups get the same treatment because the
    // watcher persists even implicit defaults to sessionStorage.
    this.needsAutoPick = true
    this._autoAdvance = false
    this._firstLoadDone = false
    if (sessionStorage.selected_group) {
      this.selectedAdGroup = JSON.parse(sessionStorage.selected_group)
    }
    if (localStorage.suggested_filter) {
      this.suggestedFilter = localStorage.suggested_filter
    }
    if (this.userAdGroups.length > 0 && !this.selectedAdGroup) {
      if (this.tenant === 'pricechopper') {
        this.selectedAdGroup = this.userAdGroups.find(obj => obj.name === 'PCE Ad Group')
      } else {
        this.selectedAdGroup = this.userAdGroups[0]
      }
    }
    if (sessionStorage.selected_store) {
      this.selectedStore = JSON.parse(sessionStorage.selected_store)
    } else { this.getStoreParties() }
    this.tableChanged = this.tableChange(this.getAdPlanners)

  },
  computed: {
    headers () {
      return [
        { sortable: false, class: 'accent', show: true },
        { text: 'Ad Name', align: 'left', sortable: true, value: 'ad_name', class: 'accent white--text', width: 'auto', show: true },
        { text: 'Ad Start - Ad End ', align: 'center', sortable: true, value: 'ad_start_date', class: 'accent white--text', width: 'auto', show: true },
        { text: 'Last Date for Shippers', align: 'start', sortable: false, value: 'last_shipper_date', class: 'accent white--text', show: !['alliance-retail-group', 'pricechopper'].includes(this.tenant) },
        { text: 'Last Order Date', align: 'start', sortable: false, value: 'last_order_date', class: 'accent white--text', show: true },
        { text: 'Ordering Status', align: 'center', sortable: false, value: 'ordering_status', class: 'accent white--text', show: true },
        { text: 'Ad Details', align: 'center', sortable: false, value: 'ad_details', class: 'accent white--text', show: true }
      ].filter(header => header.show)
    },
    currentDate() {
      return moment().format(this.$dateConfig.date_format)
    },
    disablePrevious () {
      return ((this.page - 1) * this.pageSize) === 0
    },
    disableNext () {
      return this.adPlanners.length < this.pageSize
    },
    formattedAdDate () {
      return this.adDate ? this.moment(this.adDate).format(this.$dateConfig.date_display) : ''
    },
    adGroups () {
      return this.$store.getters.adGroups
    },
    userAdGroups () {
      if (this.adGroups.length > 0) {
        let groups = this.adGroups
        if (this.isNonAdminUser) {
          // For owner retailers, include AD groups from ownerRetailerAdGroupIds
          let allowedAdGroupIds = this.userAdGroupPartyIds
          if (this.isARGOwner) {
            allowedAdGroupIds = [...this.userAdGroupPartyIds, ...this.ownerRetailerAdGroupIds]
          }
          groups = groups.filter(group => {
            return allowedAdGroupIds.includes(group?.id)
          })
        }
        if (this.limitAccessByRelatedStores) {
          let allowedIds = this.activeUserRelatedPartyIds
          if (this.isARGOwner) {
            allowedIds = [...this.activeUserRelatedPartyIds, ...this.ownerRetailerAdGroupIds]
          }
          groups = groups.filter(group => {
            return allowedIds.includes(group?.id)
          })
        }
        if (this.$auth.tenant === 'alliance-retail-group') groups = groups.filter(g => g?.attributes?.AD_GROUP_IS_ACTIVE === 'True')
        return groups.sort(this.sortByName)
      }
      return []
    },
    tenantStores () {
      return this.$store.getters.tenantStores
    },
    activeUserRelatedPartyIds () {
      return this.$store.getters.activeUserRelatedPartyIds
    },
    getsSuggestedOrders () {
      return this.$auth.tenant === 'alliance-retail-group' && this.selectedStore?.edge_subscriber === true
    },
    filteredAdPlanners () {
      if (this.suggestedFilter === "All" || !this.getsSuggestedOrders) {
        return this.adPlanners
      } else {
        return this.adPlanners.filter((ad) => ad.ad_name.toLowerCase().includes(this.suggestedFilter.toLowerCase()))
      }

    }
  },
  methods: {
    orderingDates(item, context) {
      const adStartDate = item.ad_start_date
      const { lastOrderDate, lastShipperOrderDate } = this.getFinalOrderDates(adStartDate, this.getsSuggestedOrders, item.meat_grid)
      return context === 'last_order' ? lastOrderDate : lastShipperOrderDate
    },
    statusDisplay(ad, context) {
      const adStartDate = ad.ad_start_date
      const adStatus = ad.suggested_order_status
      const { lastOrderDate, lastShipperOrderDate } = this.getFinalOrderDates(adStartDate, this.getsSuggestedOrders, ad.meat_grid)

      if (this.tenant === 'alliance-retail-group') {
        if (adStatus === 'SUBMITTED') {
          return context === 'text' ? 'Submitted' : 'blue lighten-2'
        } else if (adStatus === 'PROCESSED') {
          return context === 'text' ? 'Processed' : 'blue accent-3'
        } else if (adStatus === 'PUBLISHED') {
          return context === 'text' ? 'Published' : 'blue darken-4'
        } else if (this.currentDate <= lastOrderDate) {
          return context === 'text' ? 'Open' : 'green'
        } else {
          return context === 'text' ? 'Closed' : 'grey darken-1'
        }
      } else {
        if (adStatus === 'SUBMITTED') {
          return context === 'text' ? 'Submitted' : 'blue'
        } else if (adStatus === 'PROCESSED') {
          return context === 'text' ? 'Processed' : 'blue darken-4'
        } else if (this.currentDate <= lastShipperOrderDate) {
          return context === 'text' ? 'All Items' : 'green'
        } else if ((this.currentDate >= lastShipperOrderDate) && (this.currentDate <= lastOrderDate)) {
          return context === 'text' ? 'No Shippers' : 'orange'
        } else {
          return context === 'text' ? 'Closed' : 'grey darken-1'
        }
      }
      
    },
    async getStoreParties () {
      const partyId = this.selectedAdGroup?.id
      if (!partyId) return
      let stores = []
      try {
        if (this.tenant === 'pricechopper' && this.selectedAdGroup?.name === 'PCE Ad Group') {
          stores = this.tenantStores
        } else {
          const res = await Party.getActiveStoresByAdGroup(partyId)
          if (res?.data?.length > 0) {
            stores = res.data.map(party => {
              party.id = party.store_party_id
              party.name = `#${party.wholesaler_store_number || ''} ${party.store_name}`
              return party
            })
          }
        }
        if (this.limitAccessByRelatedStores || this.isARGOwner) {
          stores = stores.filter(result => {
            return this.userRelatedPartyIds.includes(result.id)
          })
        }
        this.storeParties = stores
        if (this.storeParties.length > 0) {
          this.selectedStore = sessionStorage.selected_store
            ? JSON.parse(sessionStorage.selected_store)
            : this.storeParties[0]
        } else this.selectedStore = null
      } catch (err) {
        this.handleError(err)
      } finally {
        this.getAdPlanners()
      }
    },
    async getAdPlanners () {
      this.loading = true
      try {
        const partyId = this.selectedAdGroup.id
        const storeId = this.selectedStore?.id
        if (!storeId) {
          this.adPlanners = []
          return
        }
        let res = null;

        if (this.getsSuggestedOrders) {
          res = await AdRetails.getSuggestedAdPlanners(partyId, storeId, this.from, this.pageSize)
        } else {
          res = await AdRetails.getAdPlanners(partyId, this.from, this.pageSize, storeId)
        }

        this.adPlanners = res?.data || []
      } catch (err) {
        this.handleError(err)
      } finally {
        this.loading = false
        this._firstLoadDone = true
        if (this.adPlanners.length > 0) {
          this.needsAutoPick = false
        } else if (this.needsAutoPick && !this.userPicked && this.userAdGroups.length > 1) {
          // Only consume the auto-pick once the group list is actually here —
          // it loads async and can lag the first (empty) planner load, in which
          // case the userAdGroups watcher below kicks the probe off instead.
          this.needsAutoPick = false
          this.autoPickGroupWithPlanners()
        }
      }
    },
    // The default (first) ad group came up with no planners: quietly probe the
    // user's other groups — first store per group, same endpoint the list uses —
    // and switch to the first group that actually has planners. Bounded, and
    // aborts the moment the user picks anything themselves.
    async autoPickGroupWithPlanners () {
      const MAX_PROBE = 12
      const currentId = this.selectedAdGroup?.id
      const candidates = this.userAdGroups.filter(g => g?.id && g.id !== currentId).slice(0, MAX_PROBE)
      for (const group of candidates) {
        if (this.userPicked) return
        try {
          let stores = []
          if (this.tenant === 'pricechopper' && group?.name === 'PCE Ad Group') {
            stores = this.tenantStores
          } else {
            const res = await Party.getActiveStoresByAdGroup(group.id)
            stores = (res?.data || []).map(party => ({ ...party, id: party.store_party_id }))
          }
          if (this.limitAccessByRelatedStores || this.isARGOwner) {
            stores = stores.filter(s => this.userRelatedPartyIds.includes(s.id))
          }
          const store = stores[0]
          if (!store) continue
          const suggested = this.$auth.tenant === 'alliance-retail-group' && store.edge_subscriber === true
          const res = suggested
            ? await AdRetails.getSuggestedAdPlanners(group.id, store.id, 0, this.pageSize)
            : await AdRetails.getAdPlanners(group.id, 0, this.pageSize, store.id)
          if ((res?.data || []).length > 0) {
            if (this.userPicked) return
            this._autoAdvance = true
            this.selectedAdGroup = group
            return
          }
        } catch (err) {
          // A group failing to probe just means we move on to the next one.
        }
      }
    },
    async createNewSuggestedOrderStatus(adPlanner) {
      try {
        const payload = {
          store_id: this.selectedStore.id,
          ad_id: adPlanner.ad_id,
          ad_start_date: adPlanner.ad_start_date,
          ad_end_date: adPlanner.ad_end_date,
          meat_grid: adPlanner.meat_grid ?? false,
          status: "DRAFT"
        }
        return await AdRetails.createSuggestedAdStatus(payload)
      } catch (err) {
        this.handleError(err)
      }
    },
    async viewAdPlanner (adPlanner) {
      if (!this.selectedAdGroup?.id || !this.selectedStore?.id) {
        return this.handleError(new Error('You must select both a store and an ad group to continue.'))
      }

      // If opening a suggested order, and no status exists yet, create one
      if (this.getsSuggestedOrders && adPlanner.suggested_order_grid_id === null) {
        const result = await this.createNewSuggestedOrderStatus(adPlanner)
        adPlanner.suggested_order_status = "DRAFT"
        adPlanner.suggested_order_grid_id = result.data.created
      }

      sessionStorage.setItem('store_id', this.selectedStore.id)
      sessionStorage.setItem('store_name', this.selectedStore.name)
      sessionStorage.setItem('edge_subscriber', JSON.stringify(this.selectedStore.edge_subscriber))
      return this.$router.push({
        name: 'RetailAdItems',
        params: {
          store_id: this.selectedStore.id,
          ad_id: adPlanner.ad_id,
          meat_grid: adPlanner.meat_grid || false,
          adPlanner: adPlanner,
          party: this.selectedAdGroup,
          store_name: this.selectedStore.name,
          edge_sub: this.selectedStore.edge_subscriber,
          storeParties: this.storeParties,
          batchStatus: this.statusDisplay(adPlanner, 'text')
        }
      })
    }
  }
}
</script>
